/**
 * TASK-231: Address Transformer
 *
 * Transforms address values from extraction format to standard Address type.
 * Handles partial addresses and various input formats.
 */

import type { Address } from '../../../types/kyc.types';
import type { ExtractedAddress } from '../types';

/**
 * Transform extracted address to standard Address format
 *
 * @param value - Extracted address (object, string, or undefined)
 * @returns Normalized Address or undefined if empty/invalid
 */
export function transformAddress(
  value: ExtractedAddress | string | unknown | undefined | null
): Address | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  // Handle string addresses (single line)
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return undefined;

    // Try to parse common single-line formats
    return parseStringAddress(trimmed);
  }

  // Handle object addresses
  if (typeof value === 'object') {
    return normalizeAddressObject(value as ExtractedAddress);
  }

  return undefined;
}

/**
 * Parse a single-line address string into Address object
 * Common formats:
 * - "123 Main St, Zurich, 8001, Switzerland"
 * - "Bahnhofstrasse 1, 8001 Zürich"
 */
function parseStringAddress(value: string): Address | undefined {
  // Split by common delimiters
  const parts = value.split(/[,\n]/).map((p) => p.trim()).filter(Boolean);

  if (parts.length === 0) return undefined;

  // Try to extract postal code and city from Swiss format "8001 Zürich"
  const postalCityPattern = /^(\d{4,5})\s+(.+)$/;

  let street = '';
  let city = '';
  let postalCode = '';
  let country = '';

  if (parts.length === 1) {
    // Single part - treat as street
    street = parts[0];
  } else if (parts.length === 2) {
    // Two parts: street, city/postal
    street = parts[0];
    const match = parts[1].match(postalCityPattern);
    if (match) {
      postalCode = match[1];
      city = match[2];
    } else {
      city = parts[1];
    }
  } else if (parts.length === 3) {
    // Three parts: street, city, country or street, postal city, country
    street = parts[0];
    const match = parts[1].match(postalCityPattern);
    if (match) {
      postalCode = match[1];
      city = match[2];
      country = parts[2];
    } else {
      city = parts[1];
      // Check if third part is postal code or country
      if (/^\d{4,5}$/.test(parts[2])) {
        postalCode = parts[2];
      } else {
        country = parts[2];
      }
    }
  } else {
    // Four or more parts: street, city, postal, country
    street = parts[0];
    city = parts[1];
    postalCode = parts[2];
    country = parts.slice(3).join(', ');
  }

  // Only return if we have at least street or city
  if (!street && !city) return undefined;

  return {
    street: street || '',
    city: city || '',
    postalCode: postalCode || '',
    country: country || '',
  };
}

/**
 * TASK-307: Unwrap value from RAG extraction format {value, confidence}
 * RAG extraction returns: { "street": { "value": "...", "confidence": 0.92 } }
 * We need plain strings: { "street": "..." }
 *
 * Handles multiple levels of nesting and ensures string output for address fields.
 */
function unwrapToString(val: unknown): string | undefined {
  if (val === undefined || val === null) return undefined;

  // Already a string - return directly
  if (typeof val === 'string') {
    return val;
  }

  // Check if it's a wrapped value object {value, confidence}
  if (typeof val === 'object' && 'value' in val) {
    const innerValue = (val as { value: unknown }).value;

    // Recursively unwrap in case of nested wrappers
    if (typeof innerValue === 'string') {
      return innerValue;
    }

    // If inner value is also an object, try to unwrap again
    if (typeof innerValue === 'object' && innerValue !== null && 'value' in innerValue) {
      return unwrapToString(innerValue);
    }

    // If inner value is not a string, try to stringify it
    if (innerValue !== null && innerValue !== undefined) {
      return String(innerValue);
    }
  }

  // Last resort: try to convert to string if it's a primitive
  if (typeof val === 'number' || typeof val === 'boolean') {
    return String(val);
  }

  return undefined;
}

/**
 * TASK-307: Recursively find address fields from deeply nested structures
 * Handles cases like: { street: { street: { value: "...", confidence: ... } } }
 */
function findAddressField(obj: unknown, fieldName: string): string | undefined {
  if (!obj || typeof obj !== 'object') return undefined;

  const record = obj as Record<string, unknown>;

  // Direct field access
  if (fieldName in record) {
    const val = record[fieldName];
    const result = unwrapToString(val);
    if (result) return result;

    // If unwrapToString returned undefined, the field might be nested further
    if (val && typeof val === 'object') {
      return findAddressField(val, fieldName);
    }
  }

  // Check if obj itself has the structure we need
  if ('value' in record) {
    const inner = record.value;
    if (inner && typeof inner === 'object') {
      return findAddressField(inner, fieldName);
    }
  }

  return undefined;
}

/**
 * Normalize an address object to standard Address format
 * TASK-307: Now handles RAG extraction format with {value, confidence} wrappers
 * Also handles deeply nested structures from malformed extraction
 */
function normalizeAddressObject(value: ExtractedAddress): Address | undefined {
  // TASK-307: First try direct unwrap, then try recursive search for nested structures
  const street = unwrapToString(value.street) || findAddressField(value, 'street');
  const streetNumber = unwrapToString(value.street_number) || findAddressField(value, 'street_number');
  const apartment = unwrapToString(value.apartment) || findAddressField(value, 'apartment');
  const city = unwrapToString(value.city) || findAddressField(value, 'city');
  const state = unwrapToString(value.state) || findAddressField(value, 'state');
  const postalCode = unwrapToString(value.postal_code) || unwrapToString((value as Record<string, unknown>).postalCode) || findAddressField(value, 'postal_code') || findAddressField(value, 'postalCode');
  const country = unwrapToString(value.country) || findAddressField(value, 'country');

  // Check if we have any meaningful data
  const hasData = street || streetNumber || city || postalCode || country;

  if (!hasData) return undefined;

  // Build street from parts (all values are guaranteed strings or undefined now)
  let streetFull = street || '';
  if (streetNumber) {
    streetFull = streetFull ? `${streetFull} ${streetNumber}` : streetNumber;
  }
  if (apartment) {
    streetFull = streetFull ? `${streetFull}, ${apartment}` : apartment;
  }

  return {
    street: streetFull ? streetFull.trim() : '',
    streetNumber: streetNumber,
    apartment: apartment,
    city: city || '',
    state: state,
    postalCode: postalCode || '',
    country: country || '',
  };
}

/**
 * Extract country from an address
 *
 * @param value - Address object or string
 * @returns Country string or undefined
 */
export function extractCountry(
  value: ExtractedAddress | Address | string | unknown | undefined | null
): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  // Handle string
  if (typeof value === 'string') {
    const address = parseStringAddress(value);
    return address?.country || undefined;
  }

  // Handle object
  if (typeof value === 'object') {
    const addr = value as ExtractedAddress | Address;
    return addr.country || undefined;
  }

  return undefined;
}

/**
 * Format address as a single-line string
 *
 * @param address - Address object
 * @returns Formatted address string
 */
export function formatAddressLine(address: Address | undefined | null): string {
  if (!address) return '';

  const parts: string[] = [];

  if (address.street) parts.push(address.street);
  if (address.city) {
    if (address.postalCode) {
      parts.push(`${address.postalCode} ${address.city}`);
    } else {
      parts.push(address.city);
    }
  } else if (address.postalCode) {
    parts.push(address.postalCode);
  }
  if (address.country) parts.push(address.country);

  return parts.join(', ');
}

/**
 * Check if an address has minimum required fields
 *
 * @param address - Address to validate
 * @returns True if address has at least street and city
 */
export function isValidAddress(address: Address | undefined | null): boolean {
  if (!address) return false;
  return Boolean(address.street && address.city);
}

/**
 * Merge two addresses, preferring non-empty values from the first
 *
 * @param primary - Primary address (takes precedence)
 * @param secondary - Secondary address (fills gaps)
 * @returns Merged address
 */
export function mergeAddresses(
  primary: Address | undefined | null,
  secondary: Address | undefined | null
): Address | undefined {
  if (!primary && !secondary) return undefined;
  if (!secondary) return primary || undefined;
  if (!primary) return secondary;

  return {
    street: primary.street || secondary.street,
    streetNumber: primary.streetNumber || secondary.streetNumber,
    apartment: primary.apartment || secondary.apartment,
    city: primary.city || secondary.city,
    state: primary.state || secondary.state,
    postalCode: primary.postalCode || secondary.postalCode,
    country: primary.country || secondary.country,
  };
}
